"use client";

import { useEffect, useMemo, useState } from "react";
import { isMuted, playSound, setMuted } from "@/lib/game/audio";
import { formatPlayDate, summarizeProgress } from "@/lib/game/stats";
import { GameIcon, GameIconButton, GamePanel, GameScreenHeader } from "../game-ui";
import { MenuScreenShell } from "../MenuScreenShell";
import { useGame } from "../GameProvider";

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <GamePanel tone="cosmic" className="!p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-100/70">
        {label}
      </p>
      <p className="mt-1 text-xl font-extrabold text-white">{value}</p>
      {hint ? <p className="mt-1 text-[10px] text-violet-100/60">{hint}</p> : null}
    </GamePanel>
  );
}

export function ProfileScreen() {
  const { goTo, progress, resetProgress, addStars } = useGame();
  const [confirmReset, setConfirmReset] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const summary = useMemo(() => summarizeProgress(progress), [progress]);

  useEffect(() => {
    setSoundOn(!isMuted());
  }, []);

  const handleReset = () => {
    resetProgress();
    setConfirmReset(false);
    goTo("home");
  };

  return (
    <MenuScreenShell variant="menu">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-6">
        <GameScreenHeader
          title="My Progress"
          subtitle="Everything you have unlocked and played so far."
          onBack={() => goTo("home")}
        />

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-5 pb-8">
          <GamePanel tone="gold">
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-white/20 bg-black/15 shadow-inner">
                <GameIcon icon="profile" size="lg" />
              </span>
              <div>
                <p className="text-lg font-extrabold text-white">Space Cadet</p>
                <p className="text-sm text-amber-100/90">
                  Playing on <strong>{summary.difficultyLabel}</strong> difficulty
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <div className="mb-1 flex justify-between text-xs font-semibold text-amber-100/90">
                  <span>Parts unlocked</span>
                  <span>
                    {summary.partsUnlocked} / {summary.partsTotal}
                  </span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-black/20 ring-1 ring-amber-200/30">
                  <div
                    className="h-full rounded-full bg-sky-400 transition-all"
                    style={{ width: `${summary.partsPercent}%` }}
                  />
                </div>
              </div>
              <div className="rounded-2xl border-2 border-amber-200/25 bg-black/15 px-3 py-2 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-100/80">
                  Star balance
                </p>
                <p className="text-xl font-extrabold text-amber-200">
                  {summary.starBalance} ⭐
                </p>
              </div>
            </div>
            <p className="mt-2 text-center text-xs text-amber-100/80">
              {summary.shopPartsOwned} / {summary.shopPartsTotal} shop parts bought
            </p>
            <div className="mt-4 flex justify-center">
              <GameIconButton
                icon="stars"
                tone="amber"
                size="lg"
                badge="+100"
                label="Add 100 stars (testing)"
                onClick={() => {
                  addStars(100);
                  playSound("unlock");
                }}
              />
            </div>
          </GamePanel>

          <section>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-violet-100/80">
              Overview
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Games played"
                value={summary.totalActivitySessions}
                hint="Activity sessions started"
              />
              <StatCard
                label="Games won"
                value={summary.totalActivityWins}
                hint="Successful completions"
              />
              <StatCard label="Launches" value={summary.rocketsLaunched} hint="Rockets flown" />
              <StatCard
                label="Hangar visits"
                value={summary.builderVisits}
                hint="Times in the builder"
              />
              <StatCard
                label="Rocket parts on ship"
                value={summary.rocketSlotsFilled}
                hint={summary.rocketFlyReady ? "Ready to launch!" : "Need nose, body & engine"}
              />
              <StatCard
                label="Stars collected"
                value={`${summary.starBalance} ⭐`}
                hint="Spend them in the Star Shop"
              />
              <StatCard
                label="Rewards earned"
                value={`${summary.totalDifficultiesEarned} / ${summary.totalDifficultiesPossible}`}
                hint="Activity difficulty tiers tried"
              />
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-violet-100/80">
              Games
            </h2>
            <ul className="flex flex-col gap-2">
              {summary.perActivity.map((activity) => (
                <li key={activity.id}>
                  <GamePanel tone="play" className="flex items-center gap-3 !p-3">
                    <span className="text-2xl" aria-hidden>
                      {activity.emoji}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold text-white">{activity.title}</span>
                      <span className="block text-xs text-violet-100/70">
                        Played {activity.sessions} · Won {activity.wins} · Tiers{" "}
                        {activity.difficultiesEarned}/3
                      </span>
                    </span>
                  </GamePanel>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-violet-100/80">
              Timeline
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="First played" value={formatPlayDate(summary.firstPlayedAt)} />
              <StatCard label="Last played" value={formatPlayDate(summary.lastPlayedAt)} />
            </div>
          </section>

          <GamePanel tone="cosmic">
            <h2 className="text-lg font-extrabold text-white">Sound</h2>
            <p className="mt-1 text-sm text-violet-100/80">
              Toggle game beeps and boops.
            </p>
            <div className="mt-4 flex justify-center">
              <GameIconButton
                icon={soundOn ? "sound-on" : "sound-off"}
                tone="slate"
                size="lg"
                onClick={() => {
                  const next = !soundOn;
                  setSoundOn(next);
                  setMuted(!next);
                  if (next) playSound("tap");
                }}
              />
            </div>
          </GamePanel>

          <GamePanel tone="cosmic" className="border-rose-300/30">
            <h2 className="text-lg font-extrabold text-rose-200">Start over</h2>
            <p className="mt-1 text-sm text-rose-100/85">
              Reset everything — stars, parts, rocket, activity progress, and all stats. This
              cannot be undone.
            </p>

            {!confirmReset ? (
              <div className="mt-4 flex justify-center">
                <GameIconButton
                  icon="reset"
                  tone="rose"
                  size="lg"
                  label="Reset all progress"
                  onClick={() => setConfirmReset(true)}
                />
              </div>
            ) : (
              <div className="mt-4 flex flex-col items-center gap-3">
                <p className="text-center text-sm font-semibold text-rose-100">
                  Are you sure? You will lose everything!
                </p>
                <div className="flex gap-3">
                  <GameIconButton
                    icon="cancel"
                    tone="slate"
                    size="md"
                    label="Cancel reset"
                    onClick={() => setConfirmReset(false)}
                  />
                  <GameIconButton
                    icon="confirm"
                    tone="rose"
                    size="md"
                    label="Yes, reset all progress"
                    onClick={handleReset}
                  />
                </div>
              </div>
            )}
          </GamePanel>
        </div>
      </div>
    </MenuScreenShell>
  );
}
